import type {Direction} from '@primer/behaviors'
import {useFocusZone} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {MARKER_NAV_KEYS} from '../constants/navigation-keys'
import type {GenericMarkerNavEl} from '../helpers/marker-navigator'
import {MarkerNavigator} from '../helpers/marker-navigator'
import type {DiffAnnotation, ThreadSummary} from '../types'

/**
 * Hook that provides keyboard navigation functionality for inline markers
 *
 * Uses arrow keys to navigate between markers
 */
export function useMarkerNavigation({
  containerRef,
  markers,
  disabled = false,
  selectedMarkerId,
}: {
  containerRef: React.RefObject<HTMLElement>
  markers: Array<ThreadSummary | GenericMarkerNavEl | DiffAnnotation>
  // onMarkerSelected: (markerId: string) => void
  disabled?: boolean
  selectedMarkerId?: string | null
}) {
  const prevMarkerNavigator = useRef<MarkerNavigator | undefined>(undefined)

  // Create a new marker navigator or use the previous one's state if available
  const markerNavigator = useMemo(
    () => new MarkerNavigator(markers, prevMarkerNavigator.current?.focusedMarker?.id || selectedMarkerId || undefined),
    [markers, selectedMarkerId],
  )

  // Store the current navigator for the next render
  useEffect(() => {
    prevMarkerNavigator.current = markerNavigator
  }, [markerNavigator])

  // Handle keyboard navigation between markers and comments
  const getNextFocusable = useCallback(
    (_direction: Direction, from: Element | undefined, event: KeyboardEvent) => {
      const currentElement = from as HTMLElement

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const key = event.key

      switch (true) {
        case key === 'ArrowUp' || key === 'ArrowDown': {
          {
            if (currentElement?.hasAttribute('data-marker-id')) {
              const nextMarker = markerNavigator.moveToNextMarker(key, from)
              if (nextMarker) {
                return document.querySelector<HTMLElement>(`[data-marker-id="${nextMarker.id}"]`) || undefined
              }
              return currentElement
            }
            if (currentElement?.hasAttribute('data-marker-navigation-comment-id')) {
              const nextMarkerItem = markerNavigator.moveToNextMarkerItem(key, from)
              if (nextMarkerItem) {
                return (
                  document.querySelector<HTMLElement>(`[data-marker-navigation-comment-id="${nextMarkerItem.id}"]`) ||
                  undefined
                )
              }
              return currentElement
            }
          }
          return currentElement
        }
        case key === 'ArrowRight': {
          const currentElementIsThreadComment = currentElement?.hasAttribute('data-marker-navigation-comment-id')

          if (currentElementIsThreadComment) return currentElement

          if (currentElement?.hasAttribute('data-marker-id')) {
            return currentElement?.querySelector<HTMLElement>('[data-first-thread-comment="true"]') || undefined
          }

          return currentElement
        }
        case key === 'ArrowLeft': {
          const currentElementIsThread = currentElement?.hasAttribute('data-marker-id')

          if (currentElementIsThread) return currentElement

          if (currentElement?.hasAttribute('data-marker-navigation-comment-id')) {
            return currentElement?.closest<HTMLElement>('[data-marker-id]') || undefined
          }

          return currentElement
        }
        default: {
          return currentElement ?? undefined
        }
      }
    },
    [markerNavigator],
  )

  // Set up focus zone for keyboard navigation
  useFocusZone(
    {
      containerRef,
      bindKeys: MARKER_NAV_KEYS,
      getNextFocusable,
      focusableElementFilter: element =>
        element.hasAttribute('data-marker-id') || element.hasAttribute('data-marker-navigation-comment-id'),
      focusInStrategy: 'previous',
      disabled,
    },
    [getNextFocusable, disabled],
  )

  return {markerNavigator}
}
